import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { useAuth } from '@/app/providers/authContext';
import { api, type ApiSchemas } from '@/lib/api';
import { queryKeys } from '@/lib/query';

// Contract types come from the generated schema, never hand-written. A backend field rename
// therefore breaks this build at the exact call site instead of rendering undefined -- which
// is the property ADR-0001 and ADR-0002 were jointly chosen for.
export type Customer = ApiSchemas['CustomerResponse'];
export type Establishment = ApiSchemas['EstablishmentResponse'];
export type OrganizationUser = ApiSchemas['UserResponse'];

const IDENTITY = '/v1/identity';

/**
 * No request carries the tenant: the API derives it from the `tenant_id` claim of the bearer
 * token and Row Level Security filters the rows. The claim is still part of every cache key so
 * a second sign-in on the same browser cannot read the first tenant's cached response.
 */
function useTenantScopedQuery<TData>(
  branch: string,
  fetcher: () => Promise<TData>,
): UseQueryResult<TData, Error> {
  const { tenantId } = useAuth();

  return useQuery({
    queryKey: [...queryKeys.organization(tenantId ?? 'sem-tenant'), branch],
    queryFn: fetcher,
    enabled: tenantId !== null,
  });
}

export function useCustomerQuery(): UseQueryResult<Customer, Error> {
  return useTenantScopedQuery('customer', () => api.get<Customer>(`${IDENTITY}/customers/me`));
}

export function useEstablishmentsQuery(): UseQueryResult<Establishment[], Error> {
  return useTenantScopedQuery('establishments', async () => {
    const page = await api.get<ApiSchemas['EstablishmentPage']>(`${IDENTITY}/establishments`);
    return [...page.items];
  });
}

export function useUsersQuery(): UseQueryResult<OrganizationUser[], Error> {
  return useTenantScopedQuery('users', async () => {
    const page = await api.get<ApiSchemas['UserPage']>(`${IDENTITY}/users`);
    return [...page.items];
  });
}

/**
 * The signed-in person's full name, for greeting them and naming the account. The access token
 * carries only the e-mail; the name lives on the tenant's user record, matched by the `sub` claim.
 * `undefined` while loading or when there is no record (a platform user outside any tenant), and
 * every caller falls back to the e-mail.
 */
export function useCurrentUserName(): string | undefined {
  const { user } = useAuth();
  const users = useUsersQuery();
  const name = users.data?.find((item) => item.id === user?.id)?.full_name.trim();
  return name === undefined || name === '' ? undefined : name;
}

/** What Sprint 5's cart, order and history screens need to know about "which establishment". */
export interface CurrentEstablishment {
  /** `undefined` while the list is loading or the caller has none yet. */
  readonly establishmentId: string | undefined;
  /** Shown in the topbar, so a user can see which unit the purchasing screens are acting for. */
  readonly establishmentName: string | undefined;
  /** How many the customer has. Above one, the topbar says which one is in force. */
  readonly establishmentCount: number;
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly error: Error | null;
}

/**
 * The establishment Sprint 5's purchasing screens act for, until there is a picker to choose one.
 *
 * The Topbar carried a disabled "Estabelecimento (em breve)" select from Sprint 1 until it was
 * removed in the v1.0.0 review: a control that cannot be used, announcing a feature by a date
 * nobody promised, is worse than no control. It now shows the name this hook resolves, which is
 * information rather than an advertisement. Switching establishments is still real future work.
 * Every demo tenant has exactly one establishment today, so the first one returned is the
 * caller's only sensible choice, and this
 * hook is the single place that decision is made: a cart, order or history screen asks it for an
 * id instead of each re-deciding "which establishment" on its own, which is what will make wiring
 * an actual picker in later a one-file change instead of a search-and-replace across three features.
 */
export function useCurrentEstablishment(): CurrentEstablishment {
  const establishments = useEstablishmentsQuery();
  // The first *active* one: a unit blocked for inactivity (or suspended) cannot place an order,
  // and acting for it while the customer has another that can would strand the buyer on a screen
  // that refuses everything. Only when none is active does the first stand — and the buying
  // screen then says why it is blocked (`BlockedUnitGuard`).
  const current =
    establishments.data?.find((establishment) => establishment.status === 'active') ??
    establishments.data?.[0];

  return {
    establishmentId: current?.id,
    establishmentName: current?.name,
    establishmentCount: establishments.data?.length ?? 0,
    isPending: establishments.isPending,
    isError: establishments.isError,
    error: establishments.error,
  };
}
